/**
 * Beat 8 part 2's presentation: the fuse, the dark, the crates, the flock, the torch.
 * The beat's fade is not here: the part module takes it from the host
 * (`BeatPartCapabilities.createFade`), as every module part does.
 *
 * Everything here runs on the RENDER step and none of it writes simulation. The
 * hens have no collider and no stealth presence, Kestner never touches her, and
 * the lid swing is a transform. What the beat reads back out are three world
 * facts — `flockSettled`, `torchGone` and `noticed` — and the part module
 * (`src/game/beats/blackoutHall.ts`) turns the first two into story flags on
 * the fixed step where the sequencer can see them.
 */

import * as THREE from 'three';
import { BLACKOUT_HALL_CUES } from '@/story/act2/blackoutHall';
import { BLACKOUT_OPEN_CRATES } from '@/world/fattening';
import { BlackoutCrates } from '@/world/blackoutCrates';
import { BlackoutTorch } from '@/world/blackoutTorch';
import { HenFlock } from '@/world/henFlock';

/** The one fitting that stays alive when the power goes. See `#torchLamp`. */
const EMERGENCY_LAMP = 'fattening-inspection-lamp';
/** What an emergency trace is: enough to see the alley by, and no more. */
const EMERGENCY_INTENSITY = 0.6;
/** Seconds the fuse takes to travel and the lights to die with it. */
const THROW_SECONDS = 1.2;

/** What `window.__rosalie.blackoutDebug` reports: the director's world facts, read live. */
export interface BlackoutHallDebug {
  /** Hens out of a crate and on the floor. Never shown to the player (spec §9). */
  readonly freed: number;
  readonly flockSettled: boolean;
  readonly torchGone: boolean;
  /** The sweep passed over her. Not a fail state — see `BlackoutTorch.noticed`. */
  readonly noticed: boolean;
}

export class BlackoutHallDirector {
  readonly #lid: THREE.Object3D;
  readonly #lever: THREE.Object3D;
  readonly #lights: readonly THREE.PointLight[];
  readonly #originals: readonly number[];
  readonly #crates = new BlackoutCrates();
  readonly #flock = new HenFlock();
  readonly #scene: THREE.Object3D;

  #torch: BlackoutTorch | null = null;
  #progress = 0;
  #throwing = false;
  /** Which openable crates have had their birds let go, so a lid does it once. */
  readonly #spilled: boolean[] = BLACKOUT_OPEN_CRATES.map(() => false);

  constructor(scene: THREE.Object3D, fuseBox: THREE.Object3D, lights: readonly THREE.PointLight[]) {
    const lid = fuseBox.getObjectByName('fuse-box-lid-pivot');
    const lever = fuseBox.getObjectByName('fuse-box-lever-pivot');
    if (!lid || !lever) throw new Error('blackout hall: fuse asset lost its live pivots');
    this.#lid = lid;
    this.#lever = lever;
    this.#lights = lights;
    this.#originals = lights.map((light) => light.intensity);
    this.#scene = scene;
    scene.add(this.#crates.group);
    scene.add(this.#flock.group);
  }

  /** Every freed bird has run itself out. The `crates` stage waits on this. */
  get flockSettled(): boolean {
    return this.#flock.settled;
  }

  /** He has gone back out through the far door. The `torch` stage waits on this. */
  get torchGone(): boolean {
    return this.#torch?.finished === true;
  }

  /** The sweep found her. Not a fail state — see `BlackoutTorch.noticed`. */
  get noticed(): boolean {
    return this.#torch?.noticed === true;
  }

  /** How many birds are out of a crate. Read by the budget gate, never by the beat. */
  get freed(): number {
    return this.#flock.freed;
  }

  /** Shove a crate lid. `openableIndex` indexes `BLACKOUT_OPEN_CRATES`, not the row. */
  openCrate(openableIndex: number): void {
    const crate = BLACKOUT_OPEN_CRATES[openableIndex];
    if (!crate) return;
    this.#crates.open(crate.index);
  }

  handle(cue: { id: string }): void {
    switch (cue.id) {
      case BLACKOUT_HALL_CUES.throwFuse:
        this.#throwing = true;
        break;
      case BLACKOUT_HALL_CUES.torchIn:
        // Built here rather than at construction, because he costs 80 draw calls
        // with his torch and the budget row for this room is measured with him in
        // it exactly once — during his own stage. A Kestner standing outside the
        // door for the whole beat would be paid for from the first frame.
        this.#torch ??= new BlackoutTorch(this.#scene, this.#torchLamp());
        break;
      default:
        break;
    }
  }

  update(frameDt: number, at: { x: number; y: number; z: number }): void {
    const dt = Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0;
    this.#crates.update(dt);
    this.#flock.update(dt);
    this.#torch?.update(dt, at);

    // A lid that has finished travelling lets its birds go, once. Driven off the
    // crate rather than off the interaction so the picture and the release cannot
    // disagree: the hens come out when the lid is actually open.
    for (let index = 0; index < BLACKOUT_OPEN_CRATES.length; index += 1) {
      const crate = BLACKOUT_OPEN_CRATES[index];
      if (!crate || this.#spilled[index] === true) continue;
      if (!this.#crates.isOpen(crate.index)) continue;
      this.#spilled[index] = true;
      this.#flock.release(index);
    }

    if (!this.#throwing) return;
    this.#progress = Math.min(1, this.#progress + dt / THROW_SECONDS);
    this.#lid.rotation.y = -this.#progress * 1.05;
    this.#lever.rotation.z = -this.#progress * 0.8;
    for (let index = 0; index < this.#lights.length; index += 1) {
      const light = this.#lights[index];
      const original = this.#originals[index];
      if (!light || original === undefined) continue;
      // The existing inspection practical becomes the emergency trace; fixture
      // count never changes, so the blackout is a lighting state rather than a
      // hidden budget improvement.
      const emergency = light.name === EMERGENCY_LAMP ? Math.min(original, EMERGENCY_INTENSITY) : 0;
      light.intensity = THREE.MathUtils.lerp(original, emergency, this.#progress);
    }

    // ── Stop, and this line is load-bearing ─────────────────────────────────
    //
    // Without it the loop above keeps running at `#progress === 1` and rewrites
    // every fitting to its blackout level on every frame — including the one
    // Kestner is carrying, which `#torchLamp` hands out later in the beat. The
    // torch would be set bright by `BlackoutTorch` and dragged back to zero here
    // a few microseconds later, for the rest of the stage, and the symptom is a
    // man walking down a dark alley holding nothing.
    //
    // The lid and lever keep their final rotations; they are set, not driven.
    if (this.#progress >= 1) this.#throwing = false;
  }

  /**
   * Which fitting Kestner carries in, and **nothing is hidden or created**.
   *
   * D-046 in the farrowing hall is the precedent and the argument: hold the light
   * count CONSTANT and swap which fittings are lit, rather than adding one. The
   * first build of this beat did the opposite — it added a sixth light and hid
   * four dead ones to pay for it — and `maxPrograms` went from 14 to 21, because
   * `numPointLights` is a program cache key and every material in the room
   * recompiled on the frame the fuse was thrown. See `blackoutTorch.ts`.
   *
   * So the torch is one of the high bays the fuse just killed. Never the
   * emergency lamp: that one is still doing its job, and taking it would leave
   * the hall with no light at all the moment he walked out of it.
   */
  #torchLamp(): THREE.PointLight {
    const dead = this.#lights.find((light) => light.name !== EMERGENCY_LAMP);
    if (!dead) throw new Error('blackout hall: no fitting for Kestner to carry');
    return dead;
  }

  dispose(): void {
    for (let index = 0; index < this.#lights.length; index += 1) {
      const light = this.#lights[index];
      const original = this.#originals[index];
      if (light && original !== undefined) light.intensity = original;
    }
    this.#torch?.dispose();
    this.#flock.dispose();
    this.#crates.dispose();
  }
}
